import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { AlertTriangle, CheckCircle2, FileWarning, Filter, Camera, User } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

const TIPO_COLORS = {
  faltante: "bg-red-100 text-red-700 border-red-200",
  extra: "bg-amber-100 text-amber-800 border-amber-200",
  dano: "bg-purple-100 text-purple-700 border-purple-200",
  retraso: "bg-blue-100 text-blue-700 border-blue-200",
};

const EXTRA_TIPOS = ["diferencia_peso","diferencia_unidades","diferencia_dimensiones","diferencia_cbm","etiqueta_incorrecta","tracking_incorrecto","destino_incorrecto","transporte_incorrecto","documentacion","otra"];

const SEV_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

export default function Incidents() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [tipoFilter, setTipoFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/incidents").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const savePhotos = async (id, photo_ids) => {
    try {
      await api.patch(`/incidents/${id}`, { photo_ids });
      toast.success(t("saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (tipoFilter !== "all") out = out.filter(r => r.tipo === tipoFilter);
    if (statusFilter !== "all") out = out.filter(r => r.status === statusFilter);
    return out;
  }, [rows, tipoFilter, statusFilter]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/incidents/${id}`, { status });
      toast.success(t("saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const stats = useMemo(() => ({
    open: rows.filter(r => r.status === "open").length,
    faltante: rows.filter(r => r.tipo === "faltante").length,
    extra: rows.filter(r => r.tipo === "extra").length,
    dano: rows.filter(r => r.tipo === "dano").length,
    total: rows.length,
  }), [rows]);

  const canManage = ["admin", "operaciones", "almacen"].includes(user.role);

  return (
    <div className="space-y-4" data-testid="incidents-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" /> {t("incidents")}
          </h2>
          <p className="text-sm text-slate-500">{stats.total} · {stats.open} {t("state").toLowerCase()}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label={t("state")} value={stats.open} color="red" onClick={() => setStatusFilter("open")} active={statusFilter === "open"} />
        <StatCard label={t("incident_faltante")} value={stats.faltante} color="red" onClick={() => setTipoFilter("faltante")} active={tipoFilter === "faltante"} />
        <StatCard label={t("incident_extra")} value={stats.extra} color="amber" onClick={() => setTipoFilter("extra")} active={tipoFilter === "extra"} />
        <StatCard label={t("incident_dano")} value={stats.dano} color="purple" onClick={() => setTipoFilter("dano")} active={tipoFilter === "dano"} />
        <StatCard label={t("total")} value={stats.total} color="slate" onClick={() => { setTipoFilter("all"); setStatusFilter("all"); }} active={tipoFilter === "all" && statusFilter === "all"} />
      </div>

      <div className="flex gap-2 flex-wrap text-xs">
        <Filter className="w-4 h-4 text-slate-400" />
        <select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} className="border border-slate-300 rounded px-2 py-1" data-testid="inc-tipo-filter">
          <option value="all">— {String(t("tipo") || "Tipo")} —</option>
          <option value="faltante">{String(t("incident_faltante"))}</option>
          <option value="extra">{String(t("incident_extra"))}</option>
          <option value="dano">{String(t("incident_dano"))}</option>
          <option value="retraso">{String(t("incident_retraso"))}</option>
          {EXTRA_TIPOS.map(k => <option key={k} value={k}>{String(t("inc_" + k))}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded px-2 py-1" data-testid="inc-status-filter">
          <option value="all">— {String(t("status"))} —</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Nº</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Tipo</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Tracking</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("description")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("severity")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("who")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("when")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("state")}</th>
                <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inc => (
                <React.Fragment key={inc.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inc-row-${inc.id}`}>
                  <td className="p-3 font-mono text-xs font-semibold">{inc.numero || "—"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${TIPO_COLORS[inc.tipo] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{String(t("incident_" + inc.tipo) || t("inc_" + inc.tipo) || inc.tipo)}</span>
                    {inc.bloquea && <span className="ms-1 text-[9px] font-bold uppercase bg-red-600 text-white px-1 py-0.5 rounded">block</span>}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    {inc.package_code || inc.tracking_code || "—"}
                    {inc.office_exit_code && <div className="text-[10px] text-indigo-600">{inc.office_exit_code}</div>}
                    {(inc.valor_esperado || inc.valor_recibido) && <div className="text-[10px] text-slate-500">{inc.valor_esperado} → {inc.valor_recibido}</div>}
                  </td>
                  <td className="p-3 text-slate-700 max-w-md truncate">{inc.descripcion}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${SEV_COLORS[inc.severity]}`}>{t("severity_" + inc.severity)}</span>
                  </td>
                  <td className="p-3 text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3 text-slate-400" />
                      <div>
                        <div className="text-xs font-medium">{inc.created_by_info?.nombre || inc.created_by_name || "—"}</div>
                        {inc.created_by_info?.role && <div className="text-[9px] uppercase tracking-wider text-slate-400">{inc.created_by_info.role}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-slate-500 tabular-nums">{new Date(inc.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${inc.status === "open" ? "bg-red-100 text-red-700" : inc.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{inc.status}</span>
                  </td>
                  <td className="p-3 text-end">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setExpanded(expanded === inc.id ? null : inc.id)} data-testid={`inc-photos-${inc.id}`} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-300 hover:border-slate-400 flex items-center gap-1">
                        <Camera className="w-3 h-3" /> {(inc.photo_ids || []).length}
                      </button>
                      {canManage && inc.status === "open" && (
                        <button onClick={() => changeStatus(inc.id, "resolved")} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`inc-resolve-${inc.id}`}>
                          <CheckCircle2 className="w-3 h-3 inline me-1" /> Resolve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded === inc.id && (
                  <tr className="bg-slate-50 border-t border-slate-100">
                    <td colSpan={9} className="p-4">
                      <PhotoUploader
                        scope="incident"
                        scopeId={inc.id}
                        value={inc.photo_ids || []}
                        onChange={(ids) => savePhotos(inc.id, ids)}
                        label={`Evidencias · ${inc.tracking_code || inc.id.slice(0, 8)}`}
                        testid={`inc-photos-uploader-${inc.id}`}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-slate-400"><FileWarning className="w-10 h-10 mx-auto opacity-30 mb-2" />—</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, onClick, active }) {
  const colors = {
    red: "border-red-500 ring-red-100",
    amber: "border-amber-500 ring-amber-100",
    purple: "border-purple-500 ring-purple-100",
    slate: "border-slate-500 ring-slate-100",
  };
  return (
    <button onClick={onClick} className={`bg-white border rounded-xl p-4 text-start hover:shadow-md transition ${active ? `${colors[color]} ring-2` : "border-slate-200"}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-display font-bold text-slate-900 tabular-nums mt-1">{value}</div>
    </button>
  );
}
